/**
 * ClassificationStatusBanner
 *
 * Mounted between the item header and the tab strip on ItemDetailPage.
 * Reflects the item's current Classification State (the fourth orthogonal
 * state machine) and surfaces actions to advance it.
 *
 * empty    — amber banner: invite user to run ATC or set manually
 * proposed — compact blue banner + "Review" button opens ClassificationReviewModal
 * approved — no banner; a small provenance pill replaces it
 */

import { useEffect, useState, useRef } from 'react';
import {
  Bot,
  CheckCircle,
  Pencil,
  ChevronDown,
  ChevronUp,
  Loader2,
  CircleDot,
  AlertCircle,
  X,
  ArrowRight,
} from 'lucide-react';
import { useItemClassificationStore } from '../../../stores/prioritization/itemClassificationStore';
import { useTemplateAgents } from '../../../hooks/useTemplateAgents';
import { useAgentReviewerAllowed } from '../../../hooks/useAgentReviewerAllowed';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { getCurrentWorkspaceId } from '../../../lib/supabaseSync';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { responsesToScopingInputs, type AltitudeQuestionResponse } from '../../../lib/altitudeQuestions';
import type { ItemClassificationProposal, ClassificationStatus } from '../../../types/prioritization/itemClassification';
import { proposerLabel, CLASSIFICATION_STATUS_LABELS } from '../../../types/prioritization/itemClassification';
import type { ItemLanguageType } from '../../../types/prioritization/lifecycle';
import { CategoryChip, AltitudeChip, CATEGORY_LABELS, ExecutionProfileChip } from '../ItemClassificationBadge';
import type { ExecutionProfile } from '../../../types/prioritization/itemClassification';

// ── Helpers ────────────────────────────────────────────────────────────────────

const CONFIDENCE_COLORS = {
  high: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]',
  medium: 'bg-[var(--mustard-dim)] text-[var(--mustard)]',
  low: 'bg-[var(--coral-dim)] text-[var(--coral)]',
};

// CategoryChip and AltitudeChip are imported from ItemClassificationBadge.
// AltitudeBadge is a local alias kept for the review modal's specific layout.
function AltitudeBadge({ level, category }: { level: number; category?: string | null }) {
  return <AltitudeChip level={level} category={category} />;
}

function QuestionResponseRow({ response }: { response: AltitudeQuestionResponse }) {
  return (
    <div className="flex items-start gap-3 text-xs">
      <span className="text-[var(--text-ter)] min-w-[6rem] capitalize">{response.dimension.replace(/_/g, ' ')}</span>
      <span className="font-semibold text-[var(--text-sec)] w-4 text-center">{response.score}</span>
      <span className="text-[var(--text-ter)]">{response.label}</span>
    </div>
  );
}

// ── Classification Review Modal ────────────────────────────────────────────────

function ClassificationReviewModal({
  itemId,
  proposal,
  onPartialAccept,
  onChange,
  onClose,
}: {
  itemId: string;
  proposal: ItemClassificationProposal;
  onPartialAccept: (acceptCategory: boolean, acceptAltitude: boolean) => void;
  onChange: () => void;
  onClose: () => void;
}) {
  const mutating = useItemClassificationStore((s) => s.byItem[itemId]?.mutating ?? false);
  const error = useItemClassificationStore((s) => s.byItem[itemId]?.error ?? null);
  const [acceptCategory, setAcceptCategory] = useState(true);
  const [acceptAltitude, setAcceptAltitude] = useState(true);
  const [showDetails, setShowDetails] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);

  const fields = proposal.classificationFields;
  const questionResponses = (fields.questionResponses ?? []) as AltitudeQuestionResponse[];
  const hasDetails = questionResponses.length > 0 || !!fields.rationale;
  const proposer = proposerLabel(proposal);
  const timestamp = new Date(proposal.createdAt).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });

  const neitherSelected = !acceptCategory && !acceptAltitude;
  const applyLabel = (() => {
    if (neitherSelected) return 'Dismiss proposal';
    if (acceptCategory && acceptAltitude) return 'Accept both';
    if (acceptCategory) return 'Accept category only';
    return 'Accept altitude only';
  })();

  // Close on overlay click
  function handleOverlayClick(e: React.MouseEvent) {
    if (e.target === overlayRef.current) onClose();
  }

  // Close on Escape
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  function handleApply() {
    onPartialAccept(acceptCategory, acceptAltitude);
  }

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={handleOverlayClick}
    >
      <div className="bg-[var(--bg-raised)] shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-[var(--rule)]">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[var(--slate-dim)] flex items-center justify-center">
              <Bot className="h-4 w-4 text-[var(--text-ter)]" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-[var(--text-pri)]">ATC Classification Proposal</h2>
              <p className="text-xs text-[var(--text-ter)] mt-0.5">
                {proposer} · {timestamp}
                {fields.confidence && (
                  <span
                    className={`ml-2 px-1.5 py-0.5 rounded text-xs font-medium ${CONFIDENCE_COLORS[fields.confidence]}`}
                  >
                    {fields.confidence} confidence
                  </span>
                )}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={mutating}
            className="p-1.5 hover:bg-[var(--hover-tint)] text-[var(--text-ter)] hover:text-[var(--text-sec)] transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-3">
          <p className="text-xs text-[var(--text-ter)] font-medium uppercase tracking-wide">
            Select what to accept
          </p>

          {/* Category row */}
          <label
            className={`flex items-center gap-3 p-3  border cursor-pointer transition-all select-none ${
              acceptCategory
                ? 'border-[var(--slate)] bg-[var(--slate-dim)] ring-1 ring-blue-200'
                : 'border-[var(--rule)] bg-[var(--bg-raised)] hover:bg-[var(--surface)]'
            }`}
          >
            <input
              type="checkbox"
              checked={acceptCategory}
              onChange={(e) => setAcceptCategory(e.target.checked)}
              disabled={mutating}
              className="h-4 w-4 rounded border-[var(--rule)] text-[var(--text-ter)] focus:ring-blue-500 disabled:opacity-50"
            />
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <span className="text-sm font-medium text-[var(--text-sec)] w-16 flex-shrink-0">Category</span>
              <CategoryChip category={fields.category} />
            </div>
          </label>

          {/* Altitude row */}
          <label
            className={`flex items-center gap-3 p-3  border cursor-pointer transition-all select-none ${
              acceptAltitude
                ? 'border-[var(--slate)] bg-[var(--slate-dim)] ring-1 ring-blue-200'
                : 'border-[var(--rule)] bg-[var(--bg-raised)] hover:bg-[var(--surface)]'
            }`}
          >
            <input
              type="checkbox"
              checked={acceptAltitude}
              onChange={(e) => setAcceptAltitude(e.target.checked)}
              disabled={mutating}
              className="h-4 w-4 rounded border-[var(--rule)] text-[var(--text-ter)] focus:ring-blue-500 disabled:opacity-50"
            />
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <span className="text-sm font-medium text-[var(--text-sec)] w-16 flex-shrink-0">Altitude</span>
              <AltitudeBadge level={fields.altitudeLevel} category={fields.category} />
            </div>
          </label>

          {/* Execution profile row — read-only, accepted automatically with the proposal */}
          {fields.executionProfile && (
            <div className="flex items-center gap-3 p-3 border border-[var(--rule)] bg-[var(--surface)]">
              <div className="h-4 w-4 flex-shrink-0" />
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <span className="text-sm font-medium text-[var(--text-ter)] w-16 flex-shrink-0">Execution</span>
                <ExecutionProfileChip profile={fields.executionProfile as ExecutionProfile} />
              </div>
            </div>
          )}

          {/* Rationale / details */}
          {hasDetails && (
            <div>
              <button
                type="button"
                onClick={() => setShowDetails((v) => !v)}
                className="flex items-center gap-1.5 text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)] transition-colors"
              >
                {showDetails ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                {showDetails ? 'Hide' : 'Show'} rationale &amp; scoring
              </button>

              {showDetails && (
                <div className="mt-2 border border-[var(--rule)] bg-[var(--surface)] px-3 py-3 space-y-3">
                  {questionResponses.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-[var(--text-sec)] mb-1.5 uppercase tracking-wide">
                        Scoping questions
                      </p>
                      <div className="space-y-1.5">
                        {questionResponses.map((r, i) => (
                          <QuestionResponseRow key={`${r.dimension}-${i}`} response={r} />
                        ))}
                      </div>
                    </div>
                  )}
                  {fields.rationale && (
                    <div>
                      <p className="text-xs font-semibold text-[var(--text-sec)] mb-1 uppercase tracking-wide">
                        Rationale
                      </p>
                      <p className="text-xs text-[var(--text-sec)] leading-relaxed">{fields.rationale}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {error && (
            <p className="text-xs text-[var(--coral)] flex items-center gap-1">
              <AlertCircle className="h-3 w-3 flex-shrink-0" />
              {error}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-4 border-t border-[var(--rule)] bg-[var(--surface)]">
          <button
            type="button"
            onClick={() => { onChange(); onClose(); }}
            disabled={mutating}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-[var(--rule)] bg-[var(--bg-raised)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)] disabled:opacity-50 transition-colors"
          >
            <Pencil className="h-3.5 w-3.5" />
            Change manually
          </button>

          <button
            type="button"
            onClick={handleApply}
            disabled={mutating}
            className={`inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold  transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              neitherSelected
                ? 'bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--rule-bright)]'
                : 'bg-[var(--slate)] text-white hover:bg-[var(--slate)]'
            }`}
          >
            {mutating ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : neitherSelected ? (
              <X className="h-3.5 w-3.5" />
            ) : (
              <CheckCircle className="h-3.5 w-3.5" />
            )}
            {mutating ? 'Applying…' : applyLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Empty state ────────────────────────────────────────────────────────────────

function EmptyState({
  itemId,
  workspaceId,
  title,
  description,
  onSetManually,
}: {
  itemId: string;
  workspaceId: string;
  title: string;
  description: string;
  onSetManually: () => void;
}) {
  const runAtcAndPropose = useItemClassificationStore((s) => s.runAtcAndPropose);
  const mutating = useItemClassificationStore((s) => s.byItem[itemId]?.mutating ?? false);
  const error = useItemClassificationStore((s) => s.byItem[itemId]?.error ?? null);
  const userId = useUserStore((s) => s.userId);
  // The Air Traffic Controller, or a custom classify agent, when on and set to run on one item.
  const classifier = useTemplateAgents('classify').find((a) => a.settings?.enabled && (a.settings.triggers as { manualSingle?: boolean } | undefined)?.manualSingle !== false) ?? null;

  async function handleRunAtc() {
    if (!classifier) return;
    await runAtcAndPropose({
      itemId, workspaceId, title, description,
      agent: { agentType: classifier.def.agentType, name: classifier.def.name },
      runByUserId: userId ?? null,
    });
  }

  return (
    <div className="border border-[var(--mustard)] bg-[var(--mustard-dim)] px-4 py-3">
      <div className="flex items-start gap-3">
        <CircleDot className="h-4 w-4 text-[var(--mustard)] mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-[var(--mustard)]">No category or altitude assigned</p>
          <p className="text-xs text-[var(--mustard)] mt-0.5">
            {classifier
              ? `Ask ${classifier.def.name} for a suggested classification, or set it manually.`
              : 'Set it manually. An administrator can turn on the Air Traffic Controller (Settings → Agents) to suggest one.'}
          </p>
          {error && (
            <p className="text-xs text-[var(--coral)] mt-1 flex items-center gap-1">
              <AlertCircle className="h-3 w-3 flex-shrink-0" />
              {error}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {classifier && (
            <button
              type="button"
              onClick={handleRunAtc}
              disabled={mutating}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[var(--mustard)] text-white hover:bg-[var(--mustard)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {mutating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Bot className="h-3.5 w-3.5" />}
              {mutating ? 'Running…' : `Ask ${classifier.def.name}`}
            </button>
          )}
          <button
            type="button"
            onClick={onSetManually}
            disabled={mutating}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-[var(--mustard)] bg-[var(--bg-raised)] text-[var(--mustard)] hover:bg-[var(--mustard-dim)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Pencil className="h-3.5 w-3.5" />
            Set manually
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Compact proposed banner ───────────────────────────────────────────────────

function ProposedBanner({
  itemId,
  proposal,
  onReview,
}: {
  itemId: string;
  proposal: ItemClassificationProposal;
  /** Null when the person may not decide this agent's proposal (its review roles). */
  onReview: (() => void) | null;
}) {
  const mutating = useItemClassificationStore((s) => s.byItem[itemId]?.mutating ?? false);
  const fields = proposal.classificationFields;
  const proposer = proposerLabel(proposal);
  const timestamp = new Date(proposal.createdAt).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });

  return (
    <div className="border border-[var(--slate)] bg-[var(--slate-dim)] px-4 py-3">
      <div className="flex items-center gap-3">
        <Bot className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center flex-wrap gap-2">
            <span className="text-sm font-semibold text-[var(--text-sec)]">Classification proposed</span>
            {fields.confidence && (
              <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${CONFIDENCE_COLORS[fields.confidence]}`}>
                {fields.confidence} confidence
              </span>
            )}
            <CategoryChip category={fields.category} />
            <AltitudeBadge level={fields.altitudeLevel} category={fields.category} />
            {fields.executionProfile && (
              <ExecutionProfileChip profile={fields.executionProfile as ExecutionProfile} />
            )}
          </div>
          <p className="text-xs text-[var(--text-sec)] mt-0.5">
            Proposed by {proposer} · {timestamp}
          </p>
        </div>
        {onReview ? (
          <button
            type="button"
            onClick={onReview}
            disabled={mutating}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
          >
            {mutating ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <ArrowRight className="h-3.5 w-3.5" />
            )}
            {mutating ? 'Applying…' : 'Review'}
          </button>
        ) : (
          <p className="text-xs text-[var(--text-sec)] max-w-[16rem] flex-shrink-0">
            Deciding this needs one of the agent’s review roles (item manager by default) or a workspace administrator.
          </p>
        )}
      </div>
    </div>
  );
}

// ── Approved state (provenance pill) ──────────────────────────────────────────

function ApprovedPill({ itemId }: { itemId: string }) {
  const [showLineage, setShowLineage] = useState(false);
  const history = useItemClassificationStore((s) => s.byItem[itemId]?.history ?? []);
  const latestAccepted = history.find((p) => p.status === 'accepted');

  const label = latestAccepted
    ? `Classified by ${proposerLabel(latestAccepted)}`
    : CLASSIFICATION_STATUS_LABELS.approved;

  return (
    <div className="flex items-center justify-between px-1 py-1">
      <button
        type="button"
        onClick={() => setShowLineage((v) => !v)}
        className="inline-flex items-center gap-1.5 px-2 py-1 text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
        title="Show classification lineage"
      >
        <CheckCircle className="h-3.5 w-3.5 text-[var(--green)]" />
        {label}
        {showLineage ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
      </button>

      {showLineage && latestAccepted && (
        <div className="mt-2 border border-[var(--rule)] bg-[var(--surface)] px-3 py-2 text-xs space-y-1 w-full">
          <p className="font-semibold text-[var(--text-sec)]">Classification lineage</p>
          <div className="flex items-center flex-wrap gap-2">
            <CategoryChip category={latestAccepted.classificationFields.category} />
            <AltitudeBadge level={latestAccepted.classificationFields.altitudeLevel} category={latestAccepted.classificationFields.category} />
            {latestAccepted.classificationFields.executionProfile && (
              <ExecutionProfileChip profile={latestAccepted.classificationFields.executionProfile as ExecutionProfile} />
            )}
          </div>
          <p className="text-[var(--text-ter)]">
            {proposerLabel(latestAccepted)} ·{' '}
            {new Date(latestAccepted.acceptedAt ?? latestAccepted.createdAt).toLocaleString()}
          </p>
        </div>
      )}
    </div>
  );
}

// ── Manual entry panel ────────────────────────────────────────────────────────

function ManualEntryPanel({
  itemId,
  workspaceId: _workspaceId,
  userId,
  prefilledCategory,
  prefilledAltitude,
  onClose,
}: {
  itemId: string;
  workspaceId: string;
  userId: string;
  prefilledCategory?: string;
  prefilledAltitude?: number;
  onClose: () => void;
}) {
  const setDirect = useItemClassificationStore((s) => s.setDirect);
  const mutating = useItemClassificationStore((s) => s.byItem[itemId]?.mutating ?? false);
  const [category, setCategory] = useState<ItemLanguageType>(
    (prefilledCategory as ItemLanguageType) ?? 'problem',
  );
  const [altitude, setAltitude] = useState<number>(prefilledAltitude ?? 3);
  const [error, setError] = useState<string | null>(null);

  const CATEGORIES: ItemLanguageType[] = [
    'problem', 'opportunity', 'risk', 'solution', 'commitment', 'investment',
  ];

  async function handleSave() {
    setError(null);
    const result = await setDirect({ itemId, category, altitudeLevel: altitude, userId });
    if (result.success) {
      onClose();
    } else {
      setError(result.error ?? 'Could not save classification');
    }
  }

  return (
    <div className="border border-[var(--rule)] bg-[var(--bg-raised)] shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-[var(--rule)]">
        <p className="text-sm font-semibold text-[var(--text-pri)]">Set classification</p>
        <p className="text-xs text-[var(--text-ter)] mt-0.5">
          Manually set category and altitude. This will be recorded as a human-approved classification.
        </p>
      </div>
      <div className="px-4 py-3 space-y-3">
        <div>
          <label className="block text-xs font-medium text-[var(--text-sec)] mb-1" htmlFor="cls-category">
            Category
          </label>
          <select
            id="cls-category"
            value={category}
            onChange={(e) => setCategory(e.target.value as ItemLanguageType)}
            className="w-full text-sm border border-[var(--rule)] px-3 py-1.5 bg-[var(--bg-raised)] focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-[var(--text-sec)] mb-1" htmlFor="cls-altitude">
            Altitude (1 = tactical, 5 = executive / portfolio)
          </label>
          <select
            id="cls-altitude"
            value={altitude}
            onChange={(e) => setAltitude(Number(e.target.value))}
            className="w-full text-sm border border-[var(--rule)] px-3 py-1.5 bg-[var(--bg-raised)] focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {[1, 2, 3, 4, 5].map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>

        {error && (
          <p className="text-xs text-[var(--coral)] flex items-center gap-1">
            <AlertCircle className="h-3 w-3 flex-shrink-0" />
            {error}
          </p>
        )}
      </div>

      <div className="px-4 py-3 border-t border-[var(--rule)] flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          disabled={mutating}
          className="px-3 py-1.5 text-xs font-medium border border-[var(--rule)] bg-[var(--bg-raised)] text-[var(--text-sec)] hover:bg-[var(--surface)] disabled:opacity-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={mutating}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:opacity-50 transition-colors"
        >
          {mutating && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          Save classification
        </button>
      </div>
    </div>
  );
}

// ── Exported component ─────────────────────────────────────────────────────────

interface ClassificationStatusBannerProps {
  itemId: string;
  /**
   * When true, skip rendering the "empty" amber alert state.
   * Use this when an AgentBar is already surfacing the ATC agent
   * to avoid showing two ways to invoke the same action.
   */
  hideEmpty?: boolean;
}

export function ClassificationStatusBanner({ itemId, hideEmpty = false }: ClassificationStatusBannerProps) {
  const fetchForItem = useItemClassificationStore((s) => s.fetchForItem);
  const accept = useItemClassificationStore((s) => s.accept);
  const partialAccept = useItemClassificationStore((s) => s.partialAccept);
  const byItem = useItemClassificationStore((s) => s.byItem[itemId]);

  const getItemById = useItemsStore((s) => s.getItemById);
  const updateItem = useItemsStore((s) => s.updateItem);
  const userId = useUserStore((s) => s.userId ?? '');

  const item = getItemById(itemId);
  const workspaceId = item?.workspaceId || item?.workspace_id || getCurrentWorkspaceId() || '';
  const title = item?.title ?? '';
  const description = item?.description ?? '';

  const [showModal, setShowModal] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [manualPrefill, setManualPrefill] = useState<{ category?: string; altitude?: number }>({});

  useEffect(() => {
    void fetchForItem(itemId);
  }, [itemId, fetchForItem]);

  const activeProposal = byItem?.activeProposal ?? null;
  const mayReview = useAgentReviewerAllowed(workspaceId, activeProposal?.proposedBy === 'agent' ? activeProposal.proposedByAgent ?? null : null);
  const history = byItem?.history ?? [];
  const loading = byItem?.loading ?? false;

  const hasApproved = history.some((p) => p.status === 'accepted');
  const classificationStatus: ClassificationStatus = (() => {
    if (activeProposal?.status === 'active') return 'proposed';
    if (hasApproved) return 'approved';
    return 'empty';
  })();

  function handlePartialAccept(acceptCategory: boolean, acceptAltitude: boolean) {
    if (!activeProposal) return;
    setShowModal(false);

    // When accepting altitude from an ATC proposal, also write the questionnaire answers
    // so the scoping fields are pre-populated (not blank) when the user next opens the item.
    if (acceptAltitude) {
      const rawResponses = activeProposal.classificationFields.questionResponses;
      if (Array.isArray(rawResponses) && rawResponses.length > 0) {
        const scopingInputs = responsesToScopingInputs(rawResponses as AltitudeQuestionResponse[]);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        void updateItem(itemId, { altitudeScopingInputs: scopingInputs } as any);
      }
    }

    if (acceptCategory && acceptAltitude) {
      void accept({ proposalId: activeProposal.id, itemId, userId });
    } else {
      void partialAccept({ proposalId: activeProposal.id, itemId, userId, acceptCategory, acceptAltitude });
    }
  }

  function handleChange() {
    const fields = activeProposal?.classificationFields;
    setManualPrefill({ category: fields?.category, altitude: fields?.altitudeLevel });
    setShowManual(true);
  }

  function handleSetManually() {
    setManualPrefill({});
    setShowManual(true);
  }

  if (loading && !byItem?.activeProposal && !byItem?.history.length) {
    return null;
  }

  // ── approved ────────────────────────────────────────────────────────────────
  if (classificationStatus === 'approved' && !showManual) {
    return <ApprovedPill itemId={itemId} />;
  }

  // ── manual entry form ───────────────────────────────────────────────────────
  if (showManual) {
    return (
      <ManualEntryPanel
        itemId={itemId}
        workspaceId={workspaceId}
        userId={userId}
        prefilledCategory={manualPrefill.category}
        prefilledAltitude={manualPrefill.altitude}
        onClose={() => setShowManual(false)}
      />
    );
  }

  // ── proposed ────────────────────────────────────────────────────────────────
  if (classificationStatus === 'proposed' && activeProposal) {
    return (
      <>
        <ProposedBanner
          itemId={itemId}
          proposal={activeProposal}
          onReview={mayReview ? () => setShowModal(true) : null}
        />
        {showModal && (
          <ClassificationReviewModal
            itemId={itemId}
            proposal={activeProposal}
            onPartialAccept={handlePartialAccept}
            onChange={handleChange}
            onClose={() => setShowModal(false)}
          />
        )}
      </>
    );
  }

  // ── empty ───────────────────────────────────────────────────────────────────
  if (hideEmpty) return null;

  return (
    <EmptyState
      itemId={itemId}
      workspaceId={workspaceId}
      title={title}
      description={description}
      onSetManually={handleSetManually}
    />
  );
}
